import { Injectable, computed, signal } from '@angular/core';

/** Workspace tabs holding unsaved edit-view changes (reported by the edit view toolbars). */
@Injectable({ providedIn: 'root' })
export class UnsavedChangesService {
  private static cpt = 0;
  // source (one per edit view toolbar) -> workspace tab id
  readonly #sources = new Map<string, string>();
  readonly dirtyTabIds = signal<ReadonlySet<string>>(new Set<string>());
  /** number of workspace tabs with unsaved changes */
  readonly dirtyCount = computed(() => this.dirtyTabIds().size);
  #allowUnload = false;

  constructor() {
    // reload / close of the browser tab with unsaved changes: let the browser ask first
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', (event: BeforeUnloadEvent) => {
        if (!this.#allowUnload && this.dirtyTabIds().size > 0) {
          event.preventDefault();
          event.returnValue = '';
        }
      });
    }
  }

  /** the page is left on purpose (logout already confirmed, session expired): no browser question */
  allowUnload(): void {
    this.#allowUnload = true;
  }

  newSourceId(): string {
    UnsavedChangesService.cpt++;
    return 'unsaved-src-' + UnsavedChangesService.cpt;
  }

  /** Only emits when the set of dirty tabs really changes. */
  setDirty(sourceId: string, tabId: string | null | undefined, dirty: boolean): void {
    const next = dirty && tabId ? tabId : undefined;
    if (this.#sources.get(sourceId) === next) {
      return;
    }
    if (next) {
      this.#sources.set(sourceId, next);
    } else {
      this.#sources.delete(sourceId);
    }
    const ids = new Set<string>(this.#sources.values());
    const prev = this.dirtyTabIds();
    if (ids.size !== prev.size || [...ids].some((id) => !prev.has(id))) {
      this.dirtyTabIds.set(ids);
    }
  }

  isTabDirty(tabId: string): boolean {
    return !!tabId && this.dirtyTabIds().has(tabId);
  }
}
